import { Component, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';

export interface KanbanColumn {
  id: string;
  title: string;
  items: KanbanItem[];
}

export interface KanbanItem {
  id: string;
  title: string;
  description?: string;
  color?: string;
  tags?: string[];
}

@Component({
  selector: 'app-kanban',
  imports: [CommonModule],
  template: `
    <div class="w-full overflow-x-auto">
      <div class="flex gap-4 min-w-max pb-4">
        @for (column of columns(); track column.id) {
          <div [class]="columnClasses">
            <div class="flex items-center justify-between mb-4">
              <h3 class="font-semibold text-gray-900 dark:text-gray-100">
                {{ column.title }}
              </h3>
              <span class="px-2 py-1 text-xs rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                {{ column.items.length }}
              </span>
            </div>
            <div class="space-y-2 min-h-[200px]">
              @for (item of column.items; track item.id) {
                <div
                  [class]="getItemClasses(item)"
                  [attr.draggable]="true"
                  (dragstart)="onDragStart($event, item, column.id)"
                  (dragover)="onDragOver($event)"
                  (drop)="onDrop($event, column.id)"
                  (dragend)="onDragEnd()">
                  <h4 class="font-medium text-gray-900 dark:text-gray-100 mb-1">
                    {{ item.title }}
                  </h4>
                  @if (item.description) {
                    <p class="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      {{ item.description }}
                    </p>
                  }
                  @if (item.tags && item.tags.length > 0) {
                    <div class="flex flex-wrap gap-1">
                      @for (tag of item.tags; track tag) {
                        <span class="px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                          {{ tag }}
                        </span>
                      }
                    </div>
                  }
                </div>
              }
            </div>
          </div>
        }
      </div>
    </div>
  `,
  styles: []
})
export class KanbanComponent {
  columns = input<KanbanColumn[]>([]);

  itemMoveEvent = output<{ item: KanbanItem; fromColumn: string; toColumn: string }>();
  columnsChangeEvent = output<KanbanColumn[]>();

  draggedItem = signal<{ item: KanbanItem; columnId: string } | null>(null);

  onDragStart(event: DragEvent, item: KanbanItem, columnId: string): void {
    this.draggedItem.set({ item, columnId });
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', item.id);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
  }

  onDrop(event: DragEvent, targetColumnId: string): void {
    event.preventDefault();
    const dragged = this.draggedItem();
    if (!dragged || dragged.columnId === targetColumnId) {
      this.onDragEnd();
      return;
    }

    const columns = this.columns().map(col => ({ ...col, items: [...col.items] }));
    const sourceColumn = columns.find(c => c.id === dragged.columnId);
    const targetColumn = columns.find(c => c.id === targetColumnId);

    if (sourceColumn && targetColumn) {
      const itemIndex = sourceColumn.items.findIndex(i => i.id === dragged.item.id);
      if (itemIndex !== -1) {
        const item = sourceColumn.items.splice(itemIndex, 1)[0];
        targetColumn.items.push(item);
        
        this.columnsChangeEvent.emit(columns);
        this.itemMoveEvent.emit({
          item: dragged.item,
          fromColumn: dragged.columnId,
          toColumn: targetColumnId
        });
      }
    }

    this.onDragEnd();
  }

  onDragEnd(): void {
    this.draggedItem.set(null);
  }

  get columnClasses(): string {
    return 'flex-shrink-0 w-64 bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700';
  }

  getItemClasses(item: KanbanItem): string {
    const classes = [
      'bg-white',
      'dark:bg-gray-700',
      'rounded-lg',
      'p-3',
      'border',
      'border-gray-200',
      'dark:border-gray-600',
      'shadow-sm',
      'cursor-move',
      'hover:shadow-md',
      'transition-shadow',
      'duration-200'
    ];

    if (item.color) {
      classes.push(`border-l-4`, `border-l-${item.color}-500`);
    }

    return classes.join(' ');
  }
}

